import type {FC} from 'react'
import {useMemo, useState, useRef} from 'react'
import type {
  HelpUrls,
  Parameter,
  Rule,
  RuleSchema,
  RuleWithSchema,
  RulesetTarget,
  ErrorRef,
  SourceType,
  ValidationError,
} from '../../types/rules-types'
import {RuleModalState} from '../../types/rules-types'
import {Blankslate} from '../Blankslate'
import {BorderBox} from '../BorderBox'
import {RuleRow} from './RuleRow'
import {getRuleSchemasByType} from '../../helpers/rule-schema'
import {DirtyState, getRuleDirtyState} from '../../helpers/dirty-state'
import {AddMetadataPatternRuleDialog} from './AddMetadataPatternRuleDialog'
import {capitalize, humanize} from '../../helpers/string'
import {CheckboxGroup} from '@primer/react'
import {PanelHeader} from './conditions/PanelHeader'
import {RestrictionsPanel} from './RestrictionsPanel'
import {useRuleStrings} from '../../hooks/use-rule-strings'

type RulesPanelProps = {
  readOnly: boolean
  helpUrls?: HelpUrls
  rulesetId?: number
  rulesetTarget: RulesetTarget
  sourceType: SourceType
  rules: Rule[]
  ruleSchemas: RuleSchema[]
  ruleErrors: Record<string, ValidationError[]>
  errorRefs: ErrorRef
  addRule: (type: string) => void
  removeRule: (rule: Rule) => void
  restoreRule: (rule: Rule) => void
  setRuleModalState: (rule: Rule, modalState: RuleModalState) => void
  updateRuleParameters: (rule: Rule, parameters: Parameter) => void
}

const COMMIT_METADATA_RESTRICTION_TYPES = [
  'commit_message_pattern',
  'commit_author_email_pattern',
  'committer_email_pattern',
]

const BRANCH_TAG_NAME_RESTRICTION_TYPES = ['branch_name_pattern', 'tag_name_pattern']

export const RulesPanel: FC<RulesPanelProps> = ({
  readOnly,
  helpUrls,
  rulesetId,
  rulesetTarget,
  sourceType,
  rules,
  ruleSchemas,
  ruleErrors,
  errorRefs,
  addRule,
  removeRule,
  restoreRule,
  setRuleModalState,
  updateRuleParameters,
}: RulesPanelProps) => {
  const [metadataRestrictionTypes, setMetadataRestrictionTypes] = useState<string[]>([])
  const {rulesOrPolicies} = useRuleStrings()

  const visibleRules = useMemo(
    () =>
      rules.filter(
        rule => rule._modalState !== RuleModalState.CREATING && getRuleDirtyState(rule) !== DirtyState.REMOVED,
      ),
    [rules],
  )

  const ruleSchemaByType = useMemo(() => getRuleSchemasByType(ruleSchemas), [ruleSchemas])

  const {refRules, commitMetadataRules: metadataRules} = useMemo(() => {
    return visibleRules.reduce<{refRules: RuleWithSchema[]; commitMetadataRules: RuleWithSchema[]}>(
      (map, rule) => {
        const ruleWithSchema = {
          ...rule,
          schema: ruleSchemaByType[rule.ruleType]!,
        }

        if (ruleWithSchema.schema.metadataPatternSchema) {
          map.commitMetadataRules.push(ruleWithSchema)
        } else {
          map.refRules.push(ruleWithSchema)
        }

        return map
      },
      {
        refRules: [],
        commitMetadataRules: [],
      },
    )
  }, [ruleSchemaByType, visibleRules])

  const {branchRuleSchemas, metadataPatternRuleSchemas} = useMemo(() => {
    return ruleSchemas.reduce<{branchRuleSchemas: RuleSchema[]; metadataPatternRuleSchemas: RuleSchema[]}>(
      (map, ruleSchema) => {
        if (ruleSchema.metadataPatternSchema) {
          map.metadataPatternRuleSchemas.push(ruleSchema)
        } else {
          map.branchRuleSchemas.push(ruleSchema)
        }

        return map
      },
      {
        branchRuleSchemas: [],
        metadataPatternRuleSchemas: [],
      },
    )
  }, [ruleSchemas])

  const availableMetadataRuleSchemas = useMemo(() => {
    return metadataPatternRuleSchemas.filter(
      ruleSchema => !metadataRules.find(rule => rule.ruleType === ruleSchema.type),
    )
  }, [metadataPatternRuleSchemas, metadataRules])

  const pendingMetadataRule = useMemo(() => {
    const existing = rules.find(
      rule =>
        rule._modalState &&
        new Set<RuleModalState>([RuleModalState.CREATING, RuleModalState.EDITING]).has(rule._modalState),
    )

    if (!existing) {
      return undefined
    }

    const ruleSchema = ruleSchemaByType[existing.ruleType]!

    if (!ruleSchema.metadataPatternSchema) {
      return undefined
    }

    return {
      ...existing,
      schema: ruleSchema,
    }
  }, [ruleSchemaByType, rules])

  const commitMetadataRestrictionButtonRef = useRef<HTMLButtonElement>(null)
  const branchTagNameRestrictionButtonRef = useRef<HTMLButtonElement>(null)

  const restrictions = useMemo(
    () => [
      {
        metadataRestrictionTypes: COMMIT_METADATA_RESTRICTION_TYPES,
        label: 'Restrict commit metadata',
        caption:
          'Restrict commit author email addresses, committer email addresses, commit message content, and other metadata',
        addRestrictionButtonRef: commitMetadataRestrictionButtonRef,
      },
      {
        metadataRestrictionTypes: BRANCH_TAG_NAME_RESTRICTION_TYPES,
        label: `Restrict ${rulesetTarget} names`,
        caption: `Restrict ${rulesetTarget} names`,
        addRestrictionButtonRef: branchTagNameRestrictionButtonRef,
      },
    ],
    [rulesetTarget],
  )

  // Find the appropriate ref for the pending metadata rule based on its type
  const returnFocusRefForPendingRule = useMemo(() => {
    if (!pendingMetadataRule) return undefined

    if (COMMIT_METADATA_RESTRICTION_TYPES.includes(pendingMetadataRule.ruleType)) {
      return commitMetadataRestrictionButtonRef
    }

    if (BRANCH_TAG_NAME_RESTRICTION_TYPES.includes(pendingMetadataRule.ruleType)) {
      return branchTagNameRestrictionButtonRef
    }

    return undefined
  }, [pendingMetadataRule])

  return (
    <div>
      <PanelHeader title={`${capitalize(rulesOrPolicies)}`} subtitle={`Which ${rulesOrPolicies} should be applied?`} />
      <div className={`d-flex flex-column gap-4`} data-testid="rules-panel">
        {(!readOnly || refRules.length) && branchRuleSchemas.length ? (
          <div>
            <ProtectionsHeader rulesetTarget={rulesetTarget} />
            <ProtectionsList
              readOnly={readOnly}
              helpUrls={helpUrls}
              rulesetId={rulesetId}
              sourceType={sourceType}
              ruleErrors={ruleErrors}
              errorRefs={errorRefs}
              addRule={addRule}
              removeRule={removeRule}
              updateRuleParameters={updateRuleParameters}
              branchRuleSchemas={branchRuleSchemas}
              refRules={refRules}
            />
          </div>
        ) : (
          <BorderBox name={`${capitalize(humanize(rulesetTarget))} protections`} showHeader>
            {(!readOnly || refRules.length) && branchRuleSchemas.length ? (
              <ul>
                <ProtectionsList
                  readOnly={readOnly}
                  helpUrls={helpUrls}
                  rulesetId={rulesetId}
                  sourceType={sourceType}
                  ruleErrors={ruleErrors}
                  addRule={addRule}
                  removeRule={removeRule}
                  updateRuleParameters={updateRuleParameters}
                  branchRuleSchemas={branchRuleSchemas}
                  refRules={refRules}
                  errorRefs={errorRefs}
                />
              </ul>
            ) : (
              <Blankslate heading={`No ${humanize(rulesetTarget)} protections have been added`} />
            )}
          </BorderBox>
        )}

        {pendingMetadataRule && (
          <AddMetadataPatternRuleDialog
            rule={pendingMetadataRule}
            ruleSchema={pendingMetadataRule.schema}
            availableMetadataRuleSchemas={availableMetadataRuleSchemas.filter(option =>
              metadataRestrictionTypes.includes(option.type),
            )}
            removeRule={removeRule}
            setRuleModalState={setRuleModalState}
            updateRuleParameters={updateRuleParameters}
            returnFocusRef={returnFocusRefForPendingRule}
          />
        )}
      </div>
      {metadataPatternRuleSchemas.length > 0 && (
        <RestrictionsPanel
          readOnly={readOnly}
          helpUrl={helpUrls?.commitMetadataRules}
          title="Metadata"
          rules={metadataRules}
          availableRuleSchemas={availableMetadataRuleSchemas}
          removeRule={removeRule}
          restoreRule={restoreRule}
          setRuleModalState={setRuleModalState}
          addRule={addRule}
          metadataPatternRuleSchemas={metadataPatternRuleSchemas}
          setMetadataRestrictionTypes={setMetadataRestrictionTypes}
          sourceType={sourceType}
          ruleErrors={ruleErrors}
          errorRefs={errorRefs}
          restrictions={restrictions}
        />
      )}
    </div>
  )
}

function ProtectionsHeader({rulesetTarget}: {rulesetTarget: RulesetTarget}) {
  const {rulesOrPolicies} = useRuleStrings()

  return (
    <div className="pt-4" id="rulesPanelProtectionsHeader">
      <PanelHeader as="h3" title={`${capitalize(humanize(rulesetTarget))} ${rulesOrPolicies}`} />
    </div>
  )
}

type ProtectionsListProps = {
  readOnly: boolean
  helpUrls?: HelpUrls
  rulesetId?: number
  sourceType: SourceType
  ruleErrors: Record<string, ValidationError[]>
  errorRefs: ErrorRef
  addRule: (type: string) => void
  removeRule: (rule: Rule) => void
  updateRuleParameters: (rule: Rule, parameters: Parameter) => void
  branchRuleSchemas: RuleSchema[]
  refRules: RuleWithSchema[]
}

export const ProtectionsList: FC<ProtectionsListProps> = ({
  readOnly,
  helpUrls,
  rulesetId,
  sourceType,
  ruleErrors,
  errorRefs,
  addRule,
  removeRule,
  updateRuleParameters,
  branchRuleSchemas,
  refRules,
}: ProtectionsListProps) => {
  return (
    <CheckboxGroup aria-labelledby="protectionsHeader">
      {branchRuleSchemas.map(ruleSchema => {
        const existing = refRules.find(rule => rule.ruleType === ruleSchema.type)

        if (readOnly && !existing) {
          return null
        }

        return (
          <div key={ruleSchema.type} className={`Box-row d-flex flex-justify-between flex-items-center gap-2 pl-0`}>
            <RuleRow
              rulesetId={rulesetId}
              sourceType={sourceType}
              helpUrls={helpUrls}
              readOnly={readOnly}
              rule={existing}
              ruleSchema={ruleSchema}
              errors={ruleErrors[ruleSchema.type] || []}
              errorRef={errorRefs[ruleSchema.type]?.errorRef}
              fieldRefs={errorRefs[ruleSchema.type]?.fields}
              onAdd={() => addRule(ruleSchema.type)}
              onRemove={() => removeRule(refRules.find(rule => rule.ruleType === ruleSchema.type)!)}
              onUpdateParameters={parameters => updateRuleParameters(existing!, parameters)}
            />
          </div>
        )
      })}
    </CheckboxGroup>
  )
}
